import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarModuleScreen, Sidebar as SidebarView, type SidebarModule } from "@ttn-shared/ui";

import { useAuthentication } from "@app/store/useAuthtentication";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { useGenericData } from "@app/store/useGenericData";

import { getEnvs } from "../utils/getEnvs";
import { moduleInfo } from "../utils/utils";
import { getExternalUrl } from "@app/utils/getExternalUrl";
import { historyRedirect } from "@app/utils/historyRedirect";

const env = getEnvs();

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const { categories, allCategory, dataBiowel } = useAuthentication((state) => state.auth);

  const { siteId, siteName } = useGenericData((store) => store.genericData);
  const { clearDataDispense } = useDeliveryData();

  const handleClickScreen = (_: SidebarModule, screen: SidebarModuleScreen) => {
    clearDataDispense();
    void navigate(screen.url);
  };

  const handleClickExternalScreen = (_: SidebarModule, screen: SidebarModuleScreen) => {
    const screens = categories?.[0]?.modules.flatMap((module) => module.functionalities);
    const currentFunctionality = screens?.find((func) => func.id === screen.id);

    if (!currentFunctionality) {
      return;
    }

    if (currentFunctionality.isExternal) {
      historyRedirect({
        path: `${getExternalUrl({
          allCategory,
          dataBiowel,
          category: moduleInfo(currentFunctionality.url, allCategory)
        })}?goTo=${currentFunctionality.path}&params=${encodeURIComponent(
          JSON.stringify({
            siteId,
            siteName
          })
        )}`,
        isInternal: false,
        useQueryParams: true
      });

      clearDataDispense();
      return;
    }

    window.location.href = `${env.URL_PROTOCOL}${env.URL_BASE}${currentFunctionality.url}`;
  };

  const sidebarModules = useMemo<SidebarModule[]>(() => {
    const currentCategory = categories?.[0];

    return (currentCategory?.modules ?? []).map((module) => ({
      id: module.id,
      label: (module.name ?? "").toUpperCase(),
      icon: module.img,
      screens: module.functionalities.map((func) => ({
        id: func.id,
        label: (func.name ?? "").toUpperCase(),
        url: func.url,
        external: !func.url.includes("/dispensation")
      }))
    }));
  }, [categories]);

  return (
    <SidebarView
      currentPathname={location.pathname}
      modules={sidebarModules}
      onClickScreen={handleClickScreen}
      onClickExternalScreen={handleClickExternalScreen}
    />
  );
}
